<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import ClipboardIcon from '@/components/clipboard-icon.vue';
import { formatDate } from '@/utils/format';
import Decimal from 'decimal.js-light';
import Chip from '@/components/chip.vue';
import { isBetCasheable } from '@/components/calculator/helper';
import type { Bet, BetSlipSelection } from '@/types/coupons';

export default defineComponent({
  name: 'row-bet',
  components: { ClipboardIcon, Chip },
  computed: {
    isCasheable() {
      return isBetCasheable(this.bet, this.betSlipSelection);
    },
  },
  methods: {
    formatDate,
    getDiffIcon(bet: any) {
      const diff = new Decimal(bet.selection.Decimal).minus(bet.odds).toNumber();
      if (bet.selection.ResultStatus === 'Winner') return 'pi-plus text-green-500';
      if (bet.selection.ResultStatus === 'Partial' || bet.selection.ResultStatus === 'Placed')
        return 'pi-plus text-yellow-500';
      if (+bet.selection.Decimal < 1.001) return 'pi-ellipsis-v text-yellow-500';
      if (diff > 1) return 'pi-angle-double-down text-red-500';
      if (diff > 0) return 'pi-angle-down text-red-500';
      if (diff < -1) return 'pi-angle-double-up text-green-500';
      if (diff < 0) return 'pi-angle-up text-green-500';
      return 'pi-ellipsis-v';
    },
  },
  props: {
    bet: { type: Object as PropType<Bet>, required: true },
    betSlipSelection: { type: Object as PropType<BetSlipSelection>, required: true },
  },
});
</script>

<template>
  <tr>
    <td class="text-center w-14 !align-middle">
      <i
        v-if="bet.selection.ResultStatus === 'Winner'"
        class="pi pi-check text-green-500 text-xl"
      />
      <i
        v-else-if="bet.selection.ResultStatus === 'Loser'"
        class="pi pi-times text-red-500 text-xl"
      />
      <i
        v-else-if="
          bet.selection.ResultStatus === 'Partial' || bet.selection.ResultStatus === 'Placed'
        "
        class="pi pi-percentage text-yellow-500 text-xl"
      />
      <i
        v-else-if="bet.selection.ResultStatus === 'Pushed'"
        class="pi pi-refresh text-yellow-500 text-xl"
      />
      <i
        v-else
        :class="{
          'pi pi-stopwatch text-gray-500 text-xl': true,
          'text-yellow-500': bet.selection.Market.Fixture.EventStatusType === 'Inprogress',
        }"
      />
    </td>
    <td style="width: 350px">
      {{ bet.selection.Market.Fixture.Competition.Name }}
      <clipboard-icon :text="bet.selection.Market.Fixture.Competition.Name" icon="pi-file-word" />
      <br />
      <span class="text-gray-500">
        {{ bet.selection.Market.Fixture.Competition.Id }}
        <clipboard-icon :text="bet.selection.Market.Fixture.Competition.Id" class="mr-1" icon-class="text-sm"/>
        {{ bet.selection.Market.Fixture.Sport.Name }},
        {{ bet.selection.Market.Fixture.Competition.Region.Name }}
      </span>
    </td>
    <td style="width: 500px">
      <router-link
        :to="{ name: 'detailed-event', params: { id: bet.selection.Market.Fixture.Id } }"
        class="text-blue-600 dark:text-blue-400 underline"
      >
        {{ bet.selection.Market.Fixture.Name }}
      </router-link>
      <clipboard-icon :text="bet.selection.Market.Fixture.Name" class="ml-1" icon="pi-file-word" />
      <br />
      <span class="text-gray-500">
        {{ bet.selection.Market.Fixture.Id }}
        <clipboard-icon :text="bet.selection.Market.Fixture.Id" icon-class="text-sm"/>
        {{ formatDate(bet.selection.Market.Fixture.StartTimeUtc, 'DD.MM.YYYY HH:mm') }}
      </span>
    </td>
    <td style="width: 300px">
      {{ bet.selection.Market.Name }}
      <chip v-if="betSlipSelection.inPlay" class="bg-red-500 dark:bg-rose-500" value="Live" />
      <chip v-else class="bg-slate-400 dark:bg-slate-500" value="PreMatch" />
      <br />
      <span class="text-gray-500">
        {{ bet.selection.Market.MarketType.Name }}
      </span>
    </td>
    <td>
      {{ bet.selection.Name }}
      <br />
      <span class="text-gray-500">
        {{ bet.selection.Outcome?.Name }}
      </span>
    </td>
    <td class="text-nowrap text-center">
      <span class="text-blue-500 dark:text-blue-400 font-bold"> ×{{ bet.odds }} </span>
      <br />
      <span class="text-gray-500"> {{ bet.numerator }} ∕ {{ bet.denominator }} </span>
    </td>
    <td
      :class="{
        'text-gray-500': !isCasheable,
        'text-yellow-500': isCasheable,
        'text-nowrap text-center': true,
      }"
    >
      <span class="font-bold"> ×{{ bet.selection.Decimal }} </span>
      <br />
      <span> {{ bet.selection.Numerator }} ∕ {{ bet.selection.Denominator }} </span>
    </td>
    <td class="text-center !align-middle">
      <i :class="{ 'pi text-xl': true, [getDiffIcon(bet)]: true }" />
    </td>
  </tr>
</template>

<style scoped lang="scss"></style>
